import { createFileRoute, Link } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import type { MouseEvent } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import {
  SANDBOX_PAGE_READ,
  SANDBOX_TOOLS,
} from '~/components/routes/sandbox-previsions/sandbox-tools'
import { Button } from '~/components/ui/button'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '~/components/ui/card'
import { useFeatureFlagEvaluator } from '~/hooks/useFeatureFlag'
import { useAbility } from '~/utils/ability'
import { withRoutePermissions } from '~/utils/rbac'
import { isPermissionSatisfied } from '~/utils/rbacModuleIndex'

/**
 * Outil interne : ressource propre depuis l'audit du 19/09 (TAX-M3). L'index reste ouvert aux
 * porteurs des gardes de ses outils : c'est le seul point d'entrée des écrans de prévision, qui
 * n'ont pas d'entrée de menu à eux.
 */
export const Route = createFileRoute('/_authenticated/sandbox-previsions/')({
  staticData: { permission: SANDBOX_PAGE_READ },
  component: SandboxPrevisionsPage,
})

withRoutePermissions(Route)

const GuideButton = ({ href }: { href: string }) => {
  const openGuide = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    window.open(href, '_blank', 'noopener,noreferrer')
  }

  return (
    <Button variant="outline" size="sm" onClick={openGuide}>
      <Download />
      Guide PDF
    </Button>
  )
}

function SandboxPrevisionsPage() {
  const isFlagEnabled = useFeatureFlagEvaluator()
  const ability = useAbility()
  // Une carte ne s'affiche que si son flag est actif ET si son écran est ouvrable.
  const tools = SANDBOX_TOOLS.filter(
    (tool) => isFlagEnabled(tool.flagKey) && isPermissionSatisfied(ability, tool.permission),
  )

  return (
    <>
      <AppHeader>
        <Title>Sandbox prévisions</Title>
      </AppHeader>
      <AppContainer>
        {tools.length === 0 && <p className="text-muted-foreground">Aucun outil activé.</p>}
        <div className="grid gap-4 md:grid-cols-2">
          {tools.map((tool) => (
            <Link key={tool.to} to={tool.to} className="group/tool rounded-4xl outline-none">
              <Card className="h-full transition-colors hover:bg-muted group-focus-visible/tool:ring-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <tool.icon className="size-5" />
                    {tool.title}
                  </CardTitle>
                  <CardDescription>{tool.description}</CardDescription>
                </CardHeader>
                {tool.guideHref && (
                  <CardFooter>
                    <GuideButton href={tool.guideHref} />
                  </CardFooter>
                )}
              </Card>
            </Link>
          ))}
        </div>
      </AppContainer>
    </>
  )
}
